import {
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
} from '../../components/WorkspaceDrawer/Settings/types';
import {
  type PrimaryLineType,
  useLinePlotDynamicConfigStore,
} from './stores/LinePlotDynamicConfigStore';

/**
 * Public readers for line-plot Statsig dynamic configs. The real evaluation
 * lives in `./stores/LinePlotDynamicConfigStore` (hydrated once per route via
 * `useInitializeLinePlotDynamicConfigStore`); these hooks are thin accessors
 * over that precomputed snapshot so panels don't re-run the cross-org
 * ranking/merging on every render.
 */

export {
  PRIMARY_LINE_TYPES,
  type PrimaryLineType,
} from './stores/LinePlotDynamicConfigStore';

/**
 * Returns the org-level `primaryLineType` from the route-hydrated store.
 */
export const usePrimaryLineType = (): PrimaryLineType =>
  useLinePlotDynamicConfigStore(state => state.primaryLineType);

/**
 * Derives a `PrimaryLineType` from a `fullFidelityMode` setting value.
 * Unlike `usePrimaryLineType` (which aggregates an org-level Statsig config
 * across the viewer's organizations), this hook's input is the
 * `fullFidelityMode` the caller already holds in hand - typically the
 * cascaded panel/workspace setting. Only two values are produced:
 * `'min-max-last'` when `fullFidelityMode` is explicitly `'min-max-last'`,
 * and `'full-fidelity'` otherwise.
 *
 * Grouped panels read groupedFullFidelityMode, a separate opt-in key set per panel.
 */
export const useFullFidelityModeLineType = ({
  fullFidelityMode,
  groupedFullFidelityMode,
  isGrouped = false,
}: {
  fullFidelityMode: FullFidelityMode | undefined;
  groupedFullFidelityMode?: FullFidelityMode;
  isGrouped?: boolean;
}): PrimaryLineType => {
  const applicable = isGrouped ? groupedFullFidelityMode : fullFidelityMode;
  if (applicable === FULL_FIDELITY_MODES.MinMaxLast) {
    return 'min-max-last';
  }
  return 'full-fidelity';
};

export const useLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(state => state.linePlotMaxRuns);
};

export const useFullScreenLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(
    state => state.fullScreenLinePlotMaxRuns
  );
};
